import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContractContext } from "../../../../../providers/contract";
import { AuthConfig } from "../../../../../setup/context/config/authConfig";
import TextInput, {
  InputType,
} from "../../../../../components/forms/textInput";
import {
  AddOnFees,
  EContractType,
  ESenderRole,
  FeeCalculationType,
} from "../../../../../setup/api/services/contract/models";
import { EContractActions } from "../../../../../setup/api/services/contractActions/enum";
import {
  doAction,
  externalDoAction,
} from "../../../../../setup/api/services/contractActions";
import { handleCompressImage } from "../../../../../setup/util/helpers";
import { uploadFile } from "../../../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../../../s3Integration/enums";
import styles from "./styles.module.scss";
import AmountCard from "../../SettlementModal/ManualSettlement/AmountCard";
import classNames from "classnames";
import Textarea from "../../../../../components/forms/textarea";
import ImageUpload from "../../../../../components/forms/imageUpload";
import { CircularProgress } from "@mui/material";
import PDFComponent from "../../../../../components/common/PDFComponent";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import UserCard from "../../../../cashouts/CashoutCard/UserCard";

const ManualSettlement = () => {
  const { t } = useTranslation();
  const { contract } = useContext(ContractContext);

  const userId = AuthConfig.getUserInfo().id;
  const userToken = AuthConfig.getToken();

  // Create initial form data with CUSTOMER, PROVIDER, and BROKER arrays
  // Each array has the same length as the corresponding array in contract.partiesProfiles
  // Each entry is an object with value, fee, and userId properties
  const initialFormData: {
    CUSTOMER: Array<{
      value: number | undefined;
      userId: number;
    }>;
    PROVIDER: Array<{
      value: number | undefined;
      userId: number;
    }>;
    BROKER: Array<{
      value: number | undefined;
      userId: number;
    }>;
  } = {
    CUSTOMER: contract?.partiesProfiles?.CUSTOMER
      ? contract.partiesProfiles.CUSTOMER.map((profile) => ({
          value: undefined,
          userId: profile.id || 0,
        }))
      : [],
    PROVIDER: contract?.partiesProfiles?.PROVIDER
      ? contract.partiesProfiles.PROVIDER.map((profile) => ({
          value: undefined,
          userId: profile.id || 0,
        }))
      : [],
    BROKER: contract?.partiesProfiles?.BROKER
      ? contract.partiesProfiles.BROKER.map((profile) => ({
          value: undefined,
          userId: profile.id || 0,
        }))
      : [],
  };

  const isClientAdmin = useMemo(() => {
    if (!userToken) return false;
    const base64Url = userToken.split(".")[1]; // Get the payload part of the token
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const data = JSON.parse(jsonPayload);
    return data?.authorities?.some((role) => role === "CLIENT_ADMIN");
  }, [userToken]);

  const [formData, setFormData] = useState(initialFormData);
  const [showSettlementError, setShowSettlementError] = useState(false);
  const [description, setDescription] = useState<string>("");
  const [entities, setEntities] = useState<
    {
      file: File;
      s3File: string;
      isLoading: boolean;
      loadingProgress: number;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);

  const buttonDisabled = useCallback(() => {
    if (loading) return true;
    if (
      ((formData.BROKER.length &&
        formData.BROKER.every((broker) => !broker.value)) ||
        !formData.BROKER.length) &&
      ((formData.CUSTOMER.length &&
        formData.CUSTOMER.every((customer) => !customer.value)) ||
        !formData.CUSTOMER.length) &&
      ((formData.PROVIDER.length &&
        formData.PROVIDER.every((provider) => !provider.value)) ||
        !formData.PROVIDER.length)
    ) {
      return true;
    }
    return false;
  }, [formData, loading]);

  const maxAmount = useMemo(() => {
    if (!contract) return 0;
    if (contract.type === EContractType.BROKERAGE_CONTRACT)
      return contract.itemPrice;
    let totalAmount = 0;
    const customers = contract.parties?.filter(
      (p) => p.role === ESenderRole.CUSTOMER
    );

    customers?.forEach((customer) => {
      let addOnFees = 0;
      const baseAmount = customer?.amount || 0;

      const currentCustomerAddOnFees = contract?.addonFeesList?.filter(
        (fee: AddOnFees) => fee?.payee === customer.phoneNumber
      );

      currentCustomerAddOnFees?.forEach((addOnFee) => {
        if (addOnFee.feeType === FeeCalculationType.FLAT) {
          addOnFees += addOnFee.amount;
        } else {
          addOnFees += (baseAmount * addOnFee.amount) / 100;
        }
      });

      totalAmount += baseAmount + addOnFees;
    });

    return totalAmount;
  }, [contract]);

  const currentTotalValues = useMemo(() => {
    let totalValues = 0;
    formData.BROKER.forEach((broker) => {
      totalValues += broker.value || 0;
    });
    formData.CUSTOMER.forEach((customer) => {
      totalValues += customer.value || 0;
    });
    formData.PROVIDER.forEach((provider) => {
      totalValues += provider.value || 0;
    });
    return totalValues;
  }, [formData]);

  const handleSettleContract = async () => {
    if (!contract) return;

    if (
      Number(currentTotalValues.toFixed(2)) !== Number(maxAmount.toFixed(2))
    ) {
      setShowSettlementError(true);
      return;
    }

    setLoading(true);

    const cashOutAmountList: {
      id: string;
      feePercentage?: number;
      amountDue: number;
    }[] = [];

    formData.BROKER.forEach((broker) => {
      if (broker.value !== undefined) {
        const userIdentification = contract.partiesProfiles?.BROKER.find(
          (br) => br.id === broker.userId
        )?.userIdentification;
        cashOutAmountList.push({
          id: isClientAdmin
            ? String(userIdentification)
            : String(broker.userId),
          feePercentage: isClientAdmin ? undefined : 0,
          amountDue: broker.value,
        });
      }
    });
    formData.PROVIDER.forEach((provider) => {
      if (provider.value !== undefined) {
        const userIdentification = contract.partiesProfiles?.PROVIDER.find(
          (pr) => pr.id === provider.userId
        )?.userIdentification;
        cashOutAmountList.push({
          id: isClientAdmin
            ? String(userIdentification)
            : String(provider.userId),
          feePercentage: isClientAdmin ? undefined : 0,
          amountDue: provider.value,
        });
      }
    });
    formData.CUSTOMER.forEach((customer) => {
      if (customer.value !== undefined) {
        const userIdentification = contract.partiesProfiles?.CUSTOMER.find(
          (cu) => cu.id === customer.userId
        )?.userIdentification;
        cashOutAmountList.push({
          id: isClientAdmin
            ? String(userIdentification)
            : String(customer.userId),
          feePercentage: isClientAdmin ? undefined : 0,
          amountDue: customer.value,
        });
      }
    });

    let cashOutAttachments: string[] | undefined = undefined;

    if (entities.length)
      cashOutAttachments = entities.map((entity) => entity?.s3File);

    try {
      const body = {
        contractId: contract?.id,
        userId: userId,
        actorRole: isClientAdmin ? ESenderRole.CLIENT_ADMIN : ESenderRole.ADMIN,
        senderId: String(contract?.senderId),
        contractAction: isClientAdmin
          ? EContractActions.SETTLE_CONTRACT
          : EContractActions.ADMIN_SETTLE_CONTRACT,
        contractType: isClientAdmin ? contract.type : undefined,
        cashOutAmountList: cashOutAmountList,
        cashOutComment: description || undefined,
        cashOutAttachments,
      };
      isClientAdmin ? await externalDoAction(body) : await doAction(body);
      setLoading(false);
      window.location.reload();
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  const handleFileUpload = useCallback(
    async (e: any) => {
      const tempEntities: {
        file: File;
        s3File: string;
        isLoading: boolean;
        loadingProgress: number;
      }[] = [...entities];
      const allowedFormats = [
        "image/x-png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
        "image/png",
        "application/pdf",
      ];
      try {
        const totalFiles =
          e?.target?.files?.length + entities.length > 10
            ? 10 - entities.length
            : e?.target?.files?.length;

        setEntities((prevEntities) => {
          return [
            ...prevEntities,
            ...Array.from(Array(totalFiles).keys()).map(() => ({
              file: undefined as unknown as File,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            })),
          ];
        });

        for (let i = 0; i < totalFiles; i++) {
          const file = e?.target?.files[i];

          if (allowedFormats.indexOf(file?.type) === -1) {
            return;
          }

          if (file?.type !== "application/pdf") {
            tempEntities.push({
              file,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            });

            const image = await handleCompressImage(file);

            if (image) {
              const entryIndex = tempEntities.findIndex(
                (entity) => entity?.file?.name === image?.name
              );

              tempEntities[entryIndex].file = image;
            }
          } else {
            tempEntities.push({
              file,
              isLoading: true,
              s3File: "",
              loadingProgress: 0,
            });
          }

          if (i === totalFiles - 1) setEntities(tempEntities);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [entities]
  );

  const uploadFiles = useCallback(async () => {
    const entityArray = [...entities];
    const uploadRequests: Promise<void>[] = [];
    for (let i = 0; i < entityArray?.length; i++) {
      const file = entityArray?.[i]?.file;

      if (
        entityArray?.[i]?.file &&
        entityArray?.[i]?.isLoading &&
        entityArray?.[i]?.loadingProgress === 0 &&
        !entityArray?.[i]?.s3File
      ) {
        const uploadRequest = uploadFile(
          file,
          EUploadImageType.CONTRACT,
          "TEMP_WAFFY_SYSTEM",
          (progress) =>
            setEntities((prevEntities) => {
              const newEntities = prevEntities?.map((entity) => {
                if (entity.file.name === file.name) {
                  return {
                    ...entity,
                    loadingProgress: Math.round(
                      (progress?.loaded / progress?.total) * 100
                    ),
                  };
                }
                return entity;
              });
              return newEntities;
            })
        ).then((url) => {
          setEntities((prevEntities) => {
            const newEntities = prevEntities?.map((entity) => {
              if (entity.file.name === file.name) {
                return {
                  file,
                  s3File: url,
                  isLoading: false,
                  loadingProgress: 0,
                };
              }

              return entity;
            });
            return newEntities;
          });
        });

        uploadRequests.push(uploadRequest);
      }
    }

    await Promise.all(uploadRequests);
  }, [entities]);

  useEffect(() => {
    if (
      entities.length > 0 &&
      entities.some(
        (entity) => entity?.isLoading && entity.loadingProgress === 0
      )
    )
      uploadFiles();
  }, [entities, uploadFiles]);

  const handleDeleteFile = useCallback(
    (index: number) => {
      const tempEntities = [...entities];
      tempEntities.splice(index, 1);
      setEntities(tempEntities);
    },
    [entities]
  );

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.error_container}>
          <div className={styles.total_amount_container}>
            <AmountCard
              title={t("lbl_remaining_amount")}
              amount={String((maxAmount - currentTotalValues).toFixed(2))}
              isError={showSettlementError}
              amountColor={showSettlementError ? "problem" : "g900"}
            />
          </div>
          {showSettlementError && (
            <p className={classNames(styles["problem"], styles["error_text"])}>
              {t("lbl_settlement_error")}
            </p>
          )}
        </div>

        {/* CUSTOMER Text Inputs */}
        <h2 className={classNames(styles["subtitle"], styles["h2"])}>
          {t("lbl_payees")}
        </h2>
        {formData.CUSTOMER.map((customer, index) => (
          <div key={`customer-${index}`} className={styles.sub_section}>
            <UserCard
              name={
                contract?.partiesProfiles?.CUSTOMER?.[index]?.profile
                  ?.fullName || ""
              }
              type={
                contract?.partiesProfiles?.CUSTOMER?.[index]?.phoneNumber || ""
              }
              image={
                contract?.partiesProfiles?.CUSTOMER?.[index]?.profile
                  ?.photoUrl || ""
              }
              isNationalIdVerified={
                contract?.partiesProfiles?.CUSTOMER?.[index]?.nationalIdVerified
              }
              isDescriptionLtr
            />
            <div className={styles.total_amount_container}>
              <TextInput
                label={t("lbl_value")}
                maxValue={maxAmount}
                type={InputType.NUMBER}
                handleChange={(e) => {
                  const newFormData = { ...formData };
                  newFormData.CUSTOMER[index].value = Number(e.target.value);
                  setFormData(newFormData);
                  setShowSettlementError(false);
                }}
                description={t("lbl_saudi_currency")}
                defaultValue={customer.value}
                preventMaxValuePass
                containerWidth="100%"
                outerContainerWidth="100%"
                placeholder="0.0"
              />
            </div>
          </div>
        ))}

        {/* PROVIDER Text Inputs */}
        <h2 className={classNames(styles["subtitle"], styles["h2"])}>
          {t("lbl_receivers")}
        </h2>
        {formData.PROVIDER.map((provider, index) => (
          <div key={`provider-${index}`} className={styles.sub_section}>
            <UserCard
              name={
                contract?.partiesProfiles?.PROVIDER?.[index]?.profile
                  ?.fullName || ""
              }
              type={
                contract?.partiesProfiles?.PROVIDER?.[index]?.phoneNumber || ""
              }
              image={
                contract?.partiesProfiles?.PROVIDER?.[index]?.profile
                  ?.photoUrl || ""
              }
              isNationalIdVerified={
                contract?.partiesProfiles?.PROVIDER?.[index]?.nationalIdVerified
              }
              isDescriptionLtr
            />
            <div className={styles.total_amount_container}>
              <TextInput
                label={t("lbl_value")}
                maxValue={maxAmount}
                type={InputType.NUMBER}
                handleChange={(e) => {
                  const newFormData = { ...formData };
                  newFormData.PROVIDER[index].value = Number(e.target.value);
                  setFormData(newFormData);
                  setShowSettlementError(false);
                }}
                description={t("lbl_saudi_currency")}
                defaultValue={provider.value}
                preventMaxValuePass
                containerWidth="100%"
                outerContainerWidth="100%"
                placeholder="0.0"
              />
            </div>
          </div>
        ))}

        {/* BROKER Text Inputs */}
        {formData.BROKER.length > 0 && (
          <h2 className={classNames(styles["subtitle"], styles["h2"])}>
            {t("lbl_brokers")}
          </h2>
        )}
        {formData.BROKER.map((broker, index) => (
          <div key={`broker-${index}`} className={styles.sub_section}>
            <UserCard
              name={
                contract?.partiesProfiles?.BROKER?.[index]?.profile?.fullName ||
                ""
              }
              type={
                contract?.partiesProfiles?.BROKER?.[index]?.phoneNumber || ""
              }
              image={
                contract?.partiesProfiles?.BROKER?.[index]?.profile?.photoUrl ||
                ""
              }
              isNationalIdVerified={
                contract?.partiesProfiles?.BROKER?.[index]?.nationalIdVerified
              }
              isDescriptionLtr
            />
            <div className={styles.total_amount_container}>
              <TextInput
                label={t("lbl_value")}
                maxValue={maxAmount}
                type={InputType.NUMBER}
                handleChange={(e) => {
                  const newFormData = { ...formData };
                  newFormData.BROKER[index].value = Number(e.target.value);
                  setFormData(newFormData);
                  setShowSettlementError(false);
                }}
                description={t("lbl_saudi_currency")}
                defaultValue={broker.value}
                preventMaxValuePass
                containerWidth="100%"
                outerContainerWidth="100%"
                placeholder="0.0"
              />
            </div>
          </div>
        ))}
        <div className={styles.horizontal_line}></div>
        <div className={styles.info_container}>
          <h2 className={styles.info_title}>
            {t("lbl_contract_details_notes")}
          </h2>
          <Textarea
            label={t("lbl_add_description")}
            required={false}
            value={description}
            maxCharacters={500}
            maxInputHeight={"170px"}
            handleChange={(e) => setDescription(e.target.value)}
            minInputHeight="100px"
          />
          <div className={styles["form-scroll"]}>
            {entities?.map((entity) => entity?.file)?.length < 10 && (
              <div key={0} style={{ marginBottom: "0.2rem" }}>
                <ImageUpload withPDF multiple handleChange={handleFileUpload} />
              </div>
            )}
            {entities?.map((entity, index) => (
              <div key={index} className={styles["uploaded_image_container"]}>
                {entity.isLoading && entity.loadingProgress >= 0 ? (
                  <div
                    className={classNames(
                      styles["uploaded_image"],
                      styles["upload_skeleton"]
                    )}>
                    <CircularProgress
                      {...(!entity?.loadingProgress
                        ? {}
                        : {
                            variant: "determinate",
                            value: entity?.loadingProgress,
                          })}
                    />
                  </div>
                ) : (
                  <>
                    {entity?.file?.type === "application/pdf" ? (
                      <PDFComponent
                        fileUrl={entity?.s3File}
                        fileName={entity?.file?.name}
                        isPreview
                      />
                    ) : (
                      <img
                        className={styles["uploaded_image"]}
                        src={entity?.s3File}
                      />
                    )}
                  </>
                )}
                {entity?.s3File && (
                  <div
                    onClick={() => handleDeleteFile(index)}
                    className={styles["delete_icon"]}>
                    <img src="/assets/images/svg/bin.svg" alt="bin" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.cta_container}>
        <PrimaryButton
          style={{
            backgroundColor: buttonDisabled() ? "#9FA6AB" : "#0D44AD",
            height: "48px",
            color: "#FFFFFF",
            cursor: buttonDisabled() ? "not-allowed" : "pointer",
            fontSize: "1rem",
            fontWeight: 600,
          }}
          onClick={handleSettleContract}
          disabled={buttonDisabled()}>
          {t("lbl_confirm_settlement")}
        </PrimaryButton>
        {/* <PrimaryButton
          style={{
            backgroundColor: "#0C4BC41A",
            height: "48px",
            maxWidth: "234px",
            color: "#0C4BC4",
            fontSize: "16px",
            fontWeight: "400",
          }}>
          {t("lbl_save_for_later")}
        </PrimaryButton> */}
      </div>
    </div>
  );
};

export default ManualSettlement;
